/* eslint-disable camelcase */
import React from 'react'
import moment from 'moment'
import Icon from '@components/icons'
import { Box } from '@mui/material'
import style from '../index.module.scss'
import { WisetackPromotionalMessageContainer } from '@app/containers/wisetack/WisetackPromotionalMessageContainer'
import { WisetackDisclosureContainer } from '@app/containers/wisetack/WisetackDisclosureContainer'
import { USDOLLAR } from '../constants'
import { useTranslation } from 'react-i18next'

const InvoicePreview = ({ invoiceData = {}, me = {}, invoiceEdit }) => {
  const { t } = useTranslation()
  const {
    customer_first_name,
    customer_last_name,
    customer_phone,
    customer_email,
    customer_address,
    customer_address2,
    customer_city,
    customer_state,
    customer_zip,
    invoice_amount,
    invoice_items,
    attachments
  } = invoiceData

  const processItems = (items) =>
    items.map((item) => (
      <React.Fragment key={item.service_name}>
        <div>
          <div className={style['item-title']}>
            <div style={{ color: '#576C77' }}>{item.service_name}</div>
            <div>{USDOLLAR.format(item.service_price_total)}</div>
          </div>
          <div className={style['item-count']}>
            <div>QTY</div>
            <div>{item.service_quantity}</div>
          </div>
          <div className={style['item-price']}>
            <div>{t('payments.rate')}</div>
            <div>{`$${item.service_price}`}</div>
          </div>
        </div>
        <div className={style['subtotal']} style={{ paddingTop: '0.625rem' }}>
          <div>{t('payments.subtotal')}</div>
          <div className={style['number']}>{USDOLLAR.format(item.service_price_total)}</div>
        </div>
      </React.Fragment>
    ))

  const handleClick = () => {
    invoiceEdit(invoiceData)
  }

  function formatBytes(inputBytes, decimalPlaces = 2) {
    if (!+inputBytes) return '0 Bytes'
    const numOfDecimalPlaces = decimalPlaces < 0 ? 0 : decimalPlaces
    const numOfBytes = Math.floor(Math.log(inputBytes) / Math.log(1024))
    return `${parseFloat((inputBytes / Math.pow(1024, numOfBytes)).toFixed(numOfDecimalPlaces))} ${
      ['Bytes', 'KB', 'MB', 'GB', 'TB', 'PB', 'EB', 'ZB', 'YB'][numOfBytes]
    }`
  }

  function formatDate(timeStamp) {
    return moment(timeStamp).format('D MMM, YYYY')
  }

  function getFileIcon(file) {
    // Handle both invoice structure (s3Key) and estimate structure (s3Info.key)
    const s3Key = file.s3Key || (file.s3Info && file.s3Info.key) || ''
    const fileName = file.name || file.originalName || file.originalname || ''
    const contentType = file.contentType || file.type || ''

    // Get the actual file extension from the filename
    const getExtension = (name) => {
      if (!name) return ''
      const lastDot = name.lastIndexOf('.')
      return lastDot !== -1 ? name.slice(lastDot).toLowerCase() : ''
    }

    const extension = getExtension(fileName) || getExtension(s3Key)

    // Check for PDF
    if (
      extension === '.pdf' ||
      s3Key.toLowerCase().includes('.pdf') ||
      contentType === 'application/pdf' ||
      fileName.toLowerCase().includes('.pdf')
    ) {
      return 'extPdf'
    }

    // Check for Word documents (.doc, .docx)
    if (
      extension === '.doc' ||
      extension === '.docx' ||
      s3Key.toLowerCase().includes('.doc') ||
      s3Key.toLowerCase().includes('.docx') ||
      contentType.includes('word') ||
      contentType.includes('document') ||
      fileName.toLowerCase().includes('.doc') ||
      fileName.toLowerCase().includes('.docx')
    ) {
      return 'extDoc'
    }

    // Default to photo for images and other files
    return 'photo'
  }

  const makeBillToContact = () => {
    return (
      <>
        {customer_first_name && customer_last_name ? (
          <div style={{ paddingBottom: '10px' }}>{`${customer_first_name} ${customer_last_name}`}</div>
        ) : null}
        {customer_address || customer_address2 || customer_city || customer_state || customer_zip ? (
          <div style={{ paddingBottom: '10px' }}>
            {customer_address || ''} {customer_address2 ? ' ' + customer_address2 : ''} <br />
            {customer_city ? customer_city + ', ' : ''} {customer_state ? customer_state + ' ' : ''}
            {customer_zip || ''}
          </div>
        ) : null}
        {customer_phone ? <div style={{ paddingBottom: '10px' }}>{customer_phone}</div> : null}
        {customer_email ? <div>{customer_email}</div> : null}
      </>
    )
  }

  return (
    <div className={style['invoice-preview-container']}>
      <div className={style['header']}>
        <div className={style['title']}>{t('payments.invoice-summary')}</div>
        {invoiceEdit && (
          <div className={style['icon-container']} onClick={handleClick} data-test="edit-invoice-form-btn">
            <Icon name="editPencilPaper" />
            <div data-test="invoice-preview-edit-btn" className={style['link']}>
              {t('payments.edit-invoice')}
            </div>
          </div>
        )}
      </div>
      <div className={style['preview-main']}>
        <div className={style['main-header']}>
          <div className={style['head']}>{`${
            me.bussiness_name ? me.bussiness_name : me.account_settings.bussiness_name
          } - ${moment().format('MMM YYYY')}`}</div>
          <div className={style['sub']}>{`${moment(
            invoiceData.scheduled_date ? invoiceData.scheduled_date : undefined
          ).format('MMMM D, YYYY')}`}</div>
        </div>
        <div className={style['bill-to']}>
          <div className={style['head']}>{t('payments.bill-to-0')}</div>
          <div data-test="invoice-preview-contact" className={style['contact']}>
            {makeBillToContact()}
          </div>
        </div>
        <div data-test="invoice-preview-item-list" className={style['item-list']}>
          {invoice_items && processItems(invoice_items)}
        </div>
        <div className={style['total']}>
          <div className={style['title']}>{t('payments.total')}</div>
          <div>{USDOLLAR.format(invoice_amount)}</div>
        </div>
        <WisetackPromotionalMessageContainer amountInCents={invoice_amount * 100} />
        <WisetackDisclosureContainer amountInCents={invoice_amount * 100} />

        {attachments && attachments.length !== 0 ? (
          <div>
            <div className={style['head']}>
              <label className="label-text" style={{ 'margin-right': '70%', color: '#576C77' }}>
                {t('payments.attachments')}
              </label>
              {/* Show workflow indicator when attachments feature is enabled */}
            </div>

            <table className={style['table-files']}>
              <thead></thead>
              <tbody>
                {attachments.map((file, i) => {
                  return (
                    <div
                      className="border rounded-sm mb-15"
                      key={file.name || file.originalName || file.originalname || i}>
                      <tr style={{ width: '100%' }}>
                        <td rowSpan={3} style={{ width: '10%' }}>
                          <Icon name={getFileIcon(file)} className={style['show-files']} />
                        </td>
                        <td style={{ width: '80%' }}>
                          <Box
                            className="text-secondary"
                            sx={{
                              paddingLeft: '3%',
                              wordBreak: 'break-word',
                              overflow: 'hidden',
                              fontSize: '14px',
                              lineHeight: '1.4'
                            }}>
                            {file.name || file.originalName || file.originalname || 'Unknown file'}
                          </Box>
                        </td>
                      </tr>
                      <tr style={{ width: '100%' }}>
                        <td>
                          {file.size > 5 * 1024 * 1024 ? (
                            <Box sx={{ paddingLeft: '3%', color: 'red', fontSize: '13px' }}>
                              {t('payments.file-size-should-be-less-than-5-mb')}
                            </Box>
                          ) : (
                            <Box sx={{ paddingLeft: '3%', fontSize: '13px' }}>
                              {formatBytes(file.size)} . {formatDate(file.uploadedAt)}
                            </Box>
                          )}
                        </td>
                      </tr>
                    </div>
                  )
                })}
              </tbody>
            </table>
          </div>
        ) : null}
      </div>
    </div>
  )
}

export default InvoicePreview
